/**
 * API client for both backends.
 *
 * The access token is short-lived (15m). On a 401 we transparently refresh once
 * and replay the request; concurrent 401s share a single refresh so a page with
 * six parallel requests doesn't fire six refreshes.
 */

import { applyEnv } from './env';

const ACCESS_KEY = 'wpos.admin.accessToken';
const REFRESH_KEY = 'wpos.admin.refreshToken';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** Authenticated but lacking the grant — distinct from "log in again". */
  get isForbidden() { return this.status === 403; }
}

export const tokens = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = tokens.refresh;
    if (!refresh) return false;
    try {
      const res = await fetch('/portal-api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so callers awaiting this promise still see it.
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();

  return refreshInFlight;
}

async function raw(path: string, init: RequestInit): Promise<Response> {
  const access = tokens.access;
  const headers = new Headers(init.headers);
  if (access) headers.set('Authorization', `Bearer ${access}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(path, { ...init, headers });
}

/** One authenticated call: refresh-and-replay on a 401, and the same
 *  session-expired handling as every other call. Returns the raw Response. */
async function send(path: string, init: RequestInit = {}): Promise<Response> {
  let res = await raw(path, init);

  if (res.status === 401 && tokens.refresh) {
    if (await refreshAccessToken()) {
      res = await raw(path, init);
    }
  }

  if (res.status === 401) {
    tokens.clear();
    // Full reload so every component drops its stale state at once.
    if (!location.pathname.startsWith('/login')) location.href = '/login';
    throw new ApiError('Session expired', 401);
  }
  return res;
}

/** The ApiError for a non-2xx response whose body has already been read. */
function failure(res: Response, body: any): ApiError {
  let message = body?.message || body?.error || res.statusText;
  // A 403 is an RBAC block, not a data error. Give every screen a clear,
  // consistent "no access" message instead of the raw "Insufficient permissions"
  // (which read as an empty screen). Include the module the role is missing.
  if (res.status === 403) {
    const mod = body?.module
      ? ` — your role doesn't have "${body.module}"${body?.required ? ` (${body.required})` : ''} access`
      : '';
    message = `You don't have access to this${mod}. If your role changed, log out and back in.`;
  }
  return new ApiError(message, res.status, body);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await send(path, init);
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) throw failure(res, body);
  return body as T;
}

/** Same auth as `request`, but the body is bytes — for authenticated file downloads. */
async function requestBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  const res = await send(path, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw failure(res, text ? safeJson(text) : null);
  }
  return res.blob();
}

function safeJson(text: string) {
  try { return JSON.parse(text); } catch { return { message: text }; }
}

const verbs = (base: string) => ({
  get:    <T>(p: string) => request<T>(base + p),
  post:   <T>(p: string, body?: any) => request<T>(base + p, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch:  <T>(p: string, body?: any) => request<T>(base + p, { method: 'PATCH', body: JSON.stringify(body) }),
  put:    <T>(p: string, body?: any) => request<T>(base + p, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(p: string) => request<T>(base + p, { method: 'DELETE' }),
});

/** Identity service — login, users, roles. */
export const portalApi = verbs('/portal-api');
/** wpcursor-backend — all the actual data. Same token, verified locally there.
 *  Paths run through applyEnv() so the Live/Dev switch reaches the right tier. */
export const backendApi = {
  upload: <T>(p: string, body: FormData) => request<T>(applyEnv('/api' + p), { method: 'POST', body }),
  get:    <T>(p: string) => request<T>(applyEnv('/api' + p)),
  /** The response body as a Blob, with the same auth as every other call. */
  getBlob: (p: string) => requestBlob(applyEnv('/api' + p)),
  post:   <T>(p: string, body?: any) => request<T>(applyEnv('/api' + p), { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch:  <T>(p: string, body?: any) => request<T>(applyEnv('/api' + p), { method: 'PATCH', body: JSON.stringify(body) }),
  put:    <T>(p: string, body?: any) => request<T>(applyEnv('/api' + p), { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(p: string) => request<T>(applyEnv('/api' + p), { method: 'DELETE' }),
};

/** Login is the one call made without a token. */
export async function login(username: string, password: string) {
  const res = await fetch('/portal-api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.message || 'Invalid credentials', res.status, body);
  tokens.set(body.accessToken, body.refreshToken);
  return body;
}
